//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Close } from '@carbon/icons-react';
import { useTeamspaceContext } from '../../TeamspaceContext';
import TenantDataService from '../../utils/TenantDataService';
import AiDataService from '../../utils/AiDataService';
import { safeWarn } from '../../utils/securityUtils';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../UserAuth';
import CustomSnackbar, { useSnackbar } from '../common/CustomSnackbar';
import { notifyIngestionTriggered } from './ingestionTriggerBus';
import IntegrationForm from './IntegrationForm';
import PropagationProgress from './PropagationProgress';
import type { IntegrationFormValues } from './integrationSchema';

/**
 * SidePanel Component
 *
 * This component renders a side panel for creating a new integration. It
 * hosts the shared, single-screen `IntegrationForm` and handles saving the
 * integration data once the form is submitted.
 *
 * Props:
 * - `isVisible`: Determines if the side panel is visible.
 * - `onClose`: Callback to close the side panel.
 * - `isNameUnique`: Function to check if the entered name is unique.
 */
interface SidePanelProps {
  isVisible: boolean;
  onClose: () => void;
  isNameUnique: (name: string, originalName?: string) => boolean;
}

const SidePanel: React.FC<SidePanelProps> = ({
  isVisible,
  onClose,
  isNameUnique,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const { teamspaceId } = useTeamspaceContext();
  const { t } = useTranslation();
  const { user, isAuthenticated } = useUserAuth();
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();
  const saveInProgressRef = useRef(false);

  const tenantDataService = new TenantDataService(
    () => user,
    () => isAuthenticated,
  );
  const aiDataService = new AiDataService(
    () => user,
    () => isAuthenticated,
  );

  useEffect(() => {
    if (isVisible) {
      closeSnackbar();
      saveInProgressRef.current = false;
      setIsSaving(false);
      setShowProgress(false);
    } else {
      saveInProgressRef.current = false;
      setIsSaving(false);
      setShowProgress(false);
      closeSnackbar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const handleSubmit = useCallback(
    async (values: IntegrationFormValues) => {
      if (!teamspaceId) {
        showSnackbar(t('error-teamspace-not-found'), 'error');
        return;
      }
      if (saveInProgressRef.current) return;
      saveInProgressRef.current = true;
      setIsSaving(true);

      try {
        const response =
          await tenantDataService.getTeamspaceIntegrations(teamspaceId);
        const existingIntegrations = response.integrations || [];

        const newIntegration = {
          ...values,
          isenabled: true,
          id: crypto.randomUUID(),
        };

        // Existing integrations keep their enabled state. Creating one used to disable
        // every other integration in the teamspace, which is wrong now that groups can be
        // assigned different integrations — adding one would have silently cut off every
        // group using the others.
        await tenantDataService.saveIntegrations(teamspaceId, {
          integrations: [...existingIntegrations, newIntegration],
        });

        try {
          const ingestResponse = await aiDataService.triggerIngestion(
            teamspaceId,
            'True',
          );
          notifyIngestionTriggered(teamspaceId);
          if (ingestResponse?.success === false) {
            safeWarn('AI integration ingestion returned warning:', {
              integrationId: newIntegration.id,
              integrationName: newIntegration.name,
              response: ingestResponse,
            });
          }
        } catch (ingestError) {
          safeWarn('AI integration ingestion failed after save:', {
            integrationId: newIntegration.id,
            integrationName: newIntegration.name,
            error: ingestError,
          });
        }

        showSnackbar(
          `${t('integration')} ${values.name} ${t('created-successfully')}`,
          'success',
        );
        // Transition to propagation progress view
        setShowProgress(true);
      } catch (error) {
        console.error('Error saving integration:', error);
        showSnackbar(t('error-creating-integration'), 'error');
      } finally {
        saveInProgressRef.current = false;
        setIsSaving(false);
      }
    },
    [teamspaceId, tenantDataService, aiDataService, showSnackbar, t, onClose],
  );

  if (!isVisible) return null;

  return (
    <>
      <div
        id="id-overlay"
        data-testid="side-panel-backdrop"
        role="presentation"
        className="fixed inset-0 top-12 z-40 bg-black/20"
        onClick={!saveInProgressRef.current ? onClose : undefined}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !saveInProgressRef.current) onClose();
        }}
      />
      <CustomSnackbar
        open={snackbarState.open}
        message={snackbarState.message}
        severity={snackbarState.severity}
        onClose={closeSnackbar}
      />
      <div
        id="id-sidePanel"
        data-testid="side-panel-container"
        className="fixed bottom-0 right-0 top-12 z-50 flex w-[502px] flex-col border-l border-[#d3d9de] bg-white shadow-xl dark:border-gray-700 dark:bg-[#262626]"
      >
        <div
          id="id-sidePanelHeader"
          data-testid="side-panel-header"
          className="flex items-center justify-between border-b border-gray-200 bg-[#f2f4f5] p-4 dark:border-gray-700 dark:bg-[#393939]"
        >
          <h2 className="text-[16px] font-semibold text-gray-700 dark:text-gray-300">
            {t('new-integration') || 'Add Integration'}
          </h2>
          <button
            id="id-closeButton"
            onClick={onClose}
            className="rounded p-1 transition-colors hover:bg-gray-300 dark:hover:bg-gray-600"
            disabled={saveInProgressRef.current}
            aria-label="Close integration panel"
          >
            <Close size={16} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {showProgress ? (
            <PropagationProgress
              teamspaceId={teamspaceId || ''}
              tenantDataService={tenantDataService}
              onDone={onClose}
            />
          ) : (
            <IntegrationForm
              mode="create"
              isNameUnique={isNameUnique}
              aiDataService={aiDataService}
              teamspaceId={teamspaceId || undefined}
              onSubmit={handleSubmit}
              onCancel={onClose}
              isSaving={isSaving}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default SidePanel;
